import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/useMediaQuery';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from '@/components/ui/drawer';
import { cn } from '@/lib/utils';

/**
 * ResponsiveDialog
 * One overlay primitive that renders a centered Dialog on desktop and a
 * bottom Drawer on mobile. Designed so any "choose options / confirm action"
 * surface uses the same API.
 *
 * Usage:
 * <ResponsiveDialog
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Edit hotspot"
 *   description="Update content and behavior"
 *   footer={<Button>Save</Button>}
 *   size="md"
 * >
 *   ...form fields...
 * </ResponsiveDialog>
 */

export interface ResponsiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  /** Modal width on desktop (sm=448 md=560 lg=720 xl=960 fullscreen). Mobile is always bottom drawer. */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen';
  /** Hide close X (useful for non-dismissible flows like processing) */
  hideClose?: boolean;
  /** Force a specific layout regardless of viewport */
  forceMode?: 'modal' | 'drawer';
  /** className applied to the inner content shell */
  className?: string;
  /**
   * Altura máxima do drawer (só mobile). Padrão 90dvh.
   *
   * dvh e não vh: no Safari do iOS, `vh` mede a tela COM a barra de
   * endereço recolhida, que é maior do que o visível na maior parte do
   * tempo. Um drawer de 90vh nasce mais alto do que cabe, e o rodapé —
   * justamente onde ficam os botões de ação — sai por baixo da interface
   * do navegador, sem rolagem que o alcance.
   */
  drawerMaxHeight?: string;
}

const sizeClass = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-xl',
  lg: 'sm:max-w-3xl',
  xl: 'sm:max-w-5xl',
  fullscreen: 'sm:max-w-[calc(100vw-3rem)] sm:h-[calc(100vh-3rem)]',
} as const;

export function ResponsiveDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  hideClose,
  forceMode,
  className,
  drawerMaxHeight = '90dvh',
  /** Override the sr-only fallback title used for a11y (when no `title` is rendered). */
  a11yTitle,
}: ResponsiveDialogProps & { a11yTitle?: string }) {
  const { t } = useTranslation();
  a11yTitle ??= t('a11y:dialog');
  const isMobile = useIsMobile();
  const useDrawer = forceMode ? forceMode === 'drawer' : isMobile;
  const hasTitle = !!title;
  const hasDescription = !!description;

  // Radix exige DialogTitle/Description ou aria-describedby={undefined}
  // pra suprimir o warning. Renderizamos o Title SEMPRE — visível quando o
  // consumer passou um, sr-only quando não. Pra Description, deixar
  // aria-describedby={undefined} resolve o warning sem precisar inventar
  // texto. Veja https://www.radix-ui.com/primitives/docs/components/dialog#title
  if (useDrawer) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent
          className={cn('flex flex-col', className)}
          style={{ maxHeight: drawerMaxHeight }}
          // Não roubar foco pra inputs ao abrir — em mobile, focar
          // input dispara teclado virtual que sobe e quebra o layout
          // do drawer. User foca manualmente quando precisa.
          onOpenAutoFocus={(e) => e.preventDefault()}
          {...(hasDescription ? {} : { 'aria-describedby': undefined })}
        >
          {hasTitle || hasDescription ? (
            <DrawerHeader className="text-left px-6 pt-2 pb-4">
              {hasTitle && (
                <DrawerTitle className="text-lg text-[var(--color-text)]">{title}</DrawerTitle>
              )}
              {hasDescription && (
                <DrawerDescription className="text-[var(--color-text-muted)]">
                  {description}
                </DrawerDescription>
              )}
            </DrawerHeader>
          ) : (
            // Fallback a11y — Vaul precisa de DrawerTitle pra leitores de tela
            <DrawerTitle className="sr-only">{a11yTitle}</DrawerTitle>
          )}
          <div className="flex-1 overflow-y-auto px-6 pb-6">{children}</div>
          {footer && (
            /* O respiro embaixo é o indicador de início do iPhone: sem ele,
               o botão principal fica sob a barrinha e o toque cai no gesto
               do sistema em vez de no botão. */
            <DrawerFooter
              className="border-t border-[var(--color-border)] flex-row gap-2 sm:gap-3"
              style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
            >
              {footer}
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex flex-col gap-0 p-0 overflow-hidden max-h-[min(90dvh,800px)]',
          sizeClass[size],
          className,
        )}
        hideClose={hideClose}
        // Mesmo motivo do drawer — evita teclado virtual subir ao abrir.
        onOpenAutoFocus={(e) => e.preventDefault()}
        {...(hasDescription ? {} : { 'aria-describedby': undefined })}
      >
        {hasTitle || hasDescription ? (
          <DialogHeader className="px-6 pt-6 pb-4 text-left border-b border-[var(--color-border)]">
            {hasTitle && (
              <DialogTitle className="text-lg tracking-tight text-[var(--color-text)]">
                {title}
              </DialogTitle>
            )}
            {hasDescription && (
              <DialogDescription className="mt-1 text-[var(--color-text-muted)]">
                {description}
              </DialogDescription>
            )}
          </DialogHeader>
        ) : (
          // Fallback a11y — Radix exige DialogTitle SEMPRE
          <DialogTitle className="sr-only">{a11yTitle}</DialogTitle>
        )}
        <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>
        {footer && (
          <DialogFooter className="px-6 py-4 border-t border-[var(--color-border)] sm:justify-end gap-2">
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ConfirmDialog vive em `responsive-confirm-dialog.tsx` — re-export pra
// facilitar imports da família responsive de um único arquivo no futuro.
export { ResponsiveConfirmDialog as ConfirmDialog } from './responsive-confirm-dialog';
